import { Redirect, RouteComponentProps, useHistory } from "react-router-dom";
import { ENGAGEMENT_REQUIREMENTS_CREATE_BY_TEMPLATE_FLOW_PATH } from "../../../paths/constants";
import { Flow } from "../../../../../shared/components/Flow";
import {
  buildRequirementDetailsPath,
  getCreateRequirementDefinitionFlowPathname,
  getCreateRequirementDefinitionPathname
} from "../../../paths/helpers";
import { ExtractStringRouteParams } from "../../../../../shared/types/router";
import { useCallback, useMemo, useState } from "react";
import { CreateRequirementDefinitionByTemplateForm } from "./CreateRequirementDefinitionByTemplateForm";
import { OnboardingStep } from "../../../../Onboarding/types";
import { CreateRequirementDefinitionFormValues } from "../../../../Requirements/types";
import { StepReviewAndCreate } from "./StepReviewAndCreate";
import { buildEngagementTypeDetailsURL } from "../../../../EngagementTypes/paths";
import { getRequirementDefinitionTemplateByKey } from "../../../utils/getRequirementDefinitionTemplateByKey";

export const RouteCreateRequirementFlow: React.FC<
  RouteComponentProps<
    ExtractStringRouteParams<
      typeof ENGAGEMENT_REQUIREMENTS_CREATE_BY_TEMPLATE_FLOW_PATH,
      string
    >,
    {},
    { engagementId: string }
  >
> = props => {
  const templateId = props.match.params.templateId;
  const state = props.location.state;
  const engagementId = state?.engagementId;
  const history = useHistory();

  const [formState, setFormState] = useState<
    CreateRequirementDefinitionFormValues | undefined
  >();

  const template = useMemo(() => {
    return getRequirementDefinitionTemplateByKey(templateId);
  }, [templateId]);

  const basePath = getCreateRequirementDefinitionFlowPathname(templateId);
  const backPath = getCreateRequirementDefinitionPathname("all");

  const onSuccess = useCallback(() => {}, []);

  const onBack = useCallback(() => {
    if (engagementId) {
      history.push(buildEngagementTypeDetailsURL(engagementId));
    } else {
      history.push(backPath);
    }
  }, []);

  const FirstStep: React.FC<OnboardingStep> = useMemo(
    () => props =>
      (
        <CreateRequirementDefinitionByTemplateForm
          formData={formState}
          onBack={props.onBack!}
          onSuccess={formValues => {
            setFormState(formValues);
            props.onNext!();
          }}
          template={template!}
        />
      ),
    [template?.key, formState]
  );

  const SecondStep: React.FC<OnboardingStep> = useMemo(
    () => props => {
      if (template && formState) {
        return (
          <StepReviewAndCreate
            onBack={props.onBack!}
            engagementId={engagementId}
            onSuccess={requirementDefinition => {
              if (engagementId) {
                history.push(buildEngagementTypeDetailsURL(engagementId));
              } else {
                history.push(
                  buildRequirementDetailsPath(
                    requirementDefinition.requirementDefinitionId
                  )
                );
              }
            }}
            formData={formState}
            template={template}
          />
        );
      } else {
        return <Redirect from="*" to={{ pathname: basePath, state }} />;
      }
    },
    [formState, template?.key, engagementId]
  );

  if (!template) {
    return <Redirect from="*" to={{ pathname: basePath, state }} />;
  }

  return (
    <Flow
      title={template!.name}
      basePath={basePath}
      steps={[
        {
          slug: "flow",
          title: "Requirement settings",
          steps: [
            {
              slug: "info-and-settings",
              title: "Info & settings",
              component: FirstStep
            },
            {
              slug: "preview-and-create",
              title: "Preview & create",
              component: SecondStep
            }
          ]
        }
      ]}
      onBack={onBack}
      onNext={onSuccess}
    />
  );
};
